import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  AUTH_TOKEN_KEY,
  ApiRequestError,
  MAX_TEAMS_PER_OPERATOR,
  canJoinAnotherTeam,
  fetchMyTeamRequestsPage,
  fetchSystemUserInfo,
  postApplyJoinTeam,
  type MyTeamRequest,
  type SystemUserInfo,
} from '@/api';
import { SubpageHeader } from '@/components/ui/SubpageHeader';

const REQUEST_PAGE_SIZE = 10;

function requestTypeLabel(type: number, fallback: string) {
  if (type === 2) return 'Exit';
  if (type === 3) return 'Join';
  return fallback || 'Request';
}

function requestStateLabel(state: number, fallback: string) {
  if (state === 0) return 'Pending';
  if (state === 1) return 'Approved';
  if (state === 2) return 'Rejected';
  return fallback || 'Unknown';
}

function requestStateClass(state: number) {
  if (state === 1) return 'bg-green-50 text-green-700 border-green-200';
  if (state === 2) return 'bg-red-50 text-red-600 border-red-200';
  return 'bg-amber-50 text-amber-700 border-amber-200';
}

function formatRequestTime(value: string) {
  if (!value) return '—';
  return value.length >= 16 ? value.slice(0, 16) : value;
}

export default function JoinTeamPage() {
  const navigate = useNavigate();
  const [profile, setProfile] = useState<SystemUserInfo | null>(null);
  const [profileLoading, setProfileLoading] = useState(true);
  const [teamCode, setTeamCode] = useState('');
  const [submitError, setSubmitError] = useState('');
  const [successMsg, setSuccessMsg] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [requests, setRequests] = useState<MyTeamRequest[]>([]);
  const [requestsTotal, setRequestsTotal] = useState(0);
  const [requestsLoading, setRequestsLoading] = useState(true);
  const [requestsLoadingMore, setRequestsLoadingMore] = useState(false);
  const [requestsError, setRequestsError] = useState('');

  useEffect(() => {
    if (!localStorage.getItem(AUTH_TOKEN_KEY)) {
      setProfileLoading(false);
      return;
    }

    let cancelled = false;
    fetchSystemUserInfo()
      .then((info) => {
        if (!cancelled) setProfile(info);
      })
      .catch(() => {
        if (!cancelled) setProfile(null);
      })
      .finally(() => {
        if (!cancelled) setProfileLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, []);

  const loadRequests = useCallback(async (page: number, append: boolean) => {
    if (append) setRequestsLoadingMore(true);
    else setRequestsLoading(true);
    setRequestsError('');
    try {
      const result = await fetchMyTeamRequestsPage({ page, pageSize: REQUEST_PAGE_SIZE });
      setRequestsTotal(result.total);
      setRequests((prev) => (append ? [...prev, ...result.items] : result.items));
    } catch (e) {
      setRequestsError(e instanceof ApiRequestError ? e.message : 'Failed to load your requests.');
      if (!append) setRequests([]);
    } finally {
      setRequestsLoading(false);
      setRequestsLoadingMore(false);
    }
  }, []);

  useEffect(() => {
    void loadRequests(1, false);
  }, [loadRequests]);

  const atTeamLimit = !profileLoading && profile != null && !canJoinAnotherTeam(profile);
  const submitDisabled = submitting || Boolean(successMsg) || profileLoading || atTeamLimit;

  const handleSubmit = async () => {
    if (atTeamLimit) return;

    const code = teamCode.trim();
    if (!code) {
      setSubmitError('Please enter a team code.');
      return;
    }

    setSubmitting(true);
    setSubmitError('');
    setSuccessMsg('');
    try {
      await postApplyJoinTeam(code);
      setSuccessMsg('Join request sent. Waiting for team admin approval.');
      void loadRequests(1, false);
      setTimeout(() => {
        navigate('/me', { replace: true, state: { refreshProfile: true } });
      }, 1500);
    } catch (e) {
      setSubmitError(e instanceof ApiRequestError ? e.message : 'Failed to submit request.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#f8f9fa] pb-6">
      <SubpageHeader
        title="Join Team"
        subtitle={`Up to ${MAX_TEAMS_PER_OPERATOR} teams per account`}
        onBack={() => navigate('/me')}
      />

      <div className="px-4 pt-3 space-y-3">
        {profileLoading ? (
          <div className="text-center py-10 text-gray-400 text-sm">
            <i className="ri-loader-4-line text-2xl animate-spin text-green-500 block mx-auto mb-2" />
            Loading...
          </div>
        ) : (
          <>
            {atTeamLimit && (
              <div className="text-xs text-amber-800 bg-amber-50 border border-amber-100 rounded-lg px-3 py-2">
                You have reached the limit of {MAX_TEAMS_PER_OPERATOR} teams and cannot join another
                team until you leave one.
              </div>
            )}
            {submitError && (
              <div className="text-xs text-red-600 bg-red-50 border border-red-100 rounded-lg px-3 py-2">
                {submitError}
              </div>
            )}
            {successMsg && (
              <div className="text-xs text-green-700 bg-green-50 border border-green-100 rounded-lg px-3 py-2">
                {successMsg}
              </div>
            )}

            <div className="bg-white rounded-lg border border-gray-200 p-3 space-y-3">
              <div className="flex items-center gap-3 bg-green-50 rounded-lg p-3">
                <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-green-100">
                  <i className="ri-links-line text-green-600 text-lg" />
                </div>
                <p className="text-gray-600 text-xs leading-relaxed">
                  Ask your team admin for the 9-character team code. Your request will be reviewed before
                  you are added.
                </p>
              </div>

              <div>
                <label className="text-gray-700 text-xs font-normal mb-1.5 block">Team code</label>
                <input
                  value={teamCode}
                  onChange={(e) => {
                    setTeamCode(e.target.value.toUpperCase());
                    setSubmitError('');
                  }}
                  placeholder="Ab12Cd34E"
                  maxLength={20}
                  disabled={atTeamLimit}
                  className="w-full h-9 px-3 rounded-lg border border-gray-200 text-sm font-mono tracking-wider uppercase outline-none focus:border-green-500 disabled:bg-gray-50 disabled:text-gray-400 placeholder:font-normal placeholder:text-xs placeholder:text-gray-400"
                />
              </div>
            </div>

            <button
              type="button"
              onClick={() => void handleSubmit()}
              disabled={submitDisabled}
              className="w-full h-9 rounded-lg bg-green-500 text-white font-medium text-xs cursor-pointer hover:bg-green-600 transition-colors disabled:opacity-70 flex items-center justify-center gap-1.5"
            >
              {submitting ? (
                <>
                  <i className="ri-loader-4-line animate-spin text-sm" />
                  Submitting...
                </>
              ) : (
                <>
                  <i className="ri-user-add-line text-sm" />
                  Request to Join
                </>
              )}
            </button>

            <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
              <div className="px-3 py-2 border-b border-gray-100">
                <h2 className="text-gray-800 font-semibold text-xs">My Requests</h2>
              </div>
              {requestsLoading ? (
                <div className="px-3 py-6 text-center text-gray-400 text-xs">
                  <i className="ri-loader-4-line animate-spin text-green-500 text-base block mx-auto mb-1" />
                  Loading requests...
                </div>
              ) : requestsError ? (
                <div className="px-3 py-3 text-xs text-red-600">{requestsError}</div>
              ) : requests.length === 0 ? (
                <div className="px-3 py-6 text-center text-gray-400 text-xs">No join or exit requests yet.</div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="text-left text-gray-400 border-b border-gray-100">
                        <th className="px-3 py-2 font-normal">Team</th>
                        <th className="px-2 py-2 font-normal whitespace-nowrap">Type</th>
                        <th className="px-2 py-2 font-normal whitespace-nowrap">Status</th>
                        <th className="px-2 py-2 font-normal">Reason</th>
                        <th className="px-3 py-2 font-normal text-right whitespace-nowrap">Time</th>
                      </tr>
                    </thead>
                    <tbody>
                      {requests.map((row) => (
                        <tr key={row.id} className="border-b border-gray-50 last:border-b-0">
                          <td className="px-3 py-2 text-gray-800 max-w-[7.5rem]">
                            <span className="block truncate">{row.teamName || '—'}</span>
                          </td>
                          <td className="px-2 py-2 whitespace-nowrap">
                            <span
                              className={`inline-flex px-1.5 py-0.5 rounded border text-[10px] font-medium ${
                                row.requestType === 2
                                  ? 'bg-orange-50 text-orange-600 border-orange-200'
                                  : 'bg-green-50 text-green-700 border-green-200'
                              }`}
                            >
                              {requestTypeLabel(row.requestType, row.requestTypeText)}
                            </span>
                          </td>
                          <td className="px-2 py-2 whitespace-nowrap">
                            <span
                              className={`inline-flex px-1.5 py-0.5 rounded border text-[10px] font-medium ${requestStateClass(row.state)}`}
                            >
                              {requestStateLabel(row.state, row.stateText)}
                            </span>
                          </td>
                          <td className="px-2 py-2 text-red-500 text-[11px] max-w-[8rem]">
                            {row.state === 2 && row.rejectReason ? (
                              <span className="block leading-snug">{row.rejectReason}</span>
                            ) : (
                              <span className="text-gray-300">—</span>
                            )}
                          </td>
                          <td className="px-3 py-2 text-right text-gray-400 whitespace-nowrap text-[11px]">
                            {formatRequestTime(row.createTime)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
              {!requestsLoading && requests.length < requestsTotal && (
                <button
                  type="button"
                  disabled={requestsLoadingMore}
                  onClick={() =>
                    void loadRequests(Math.floor(requests.length / REQUEST_PAGE_SIZE) + 1, true)
                  }
                  className="w-full h-9 border-t border-gray-100 text-gray-500 text-xs cursor-pointer hover:bg-gray-50 disabled:opacity-60"
                >
                  {requestsLoadingMore ? 'Loading...' : 'Load more'}
                </button>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
